import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Select from '@/components/common/Select'
import Button from '@/components/common/Button'

function AISteeringPanel({ teamId }) {
  const [settings, setSettings] = useState({
    include_schedule: true,
    include_coach_notes: true,
    tone: 'casual',
    length: 'standard',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    fetchSettings()
  }, [teamId])

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_settings')
        .select('*')
        .eq('team_id', teamId)
        .single()

      if (error) throw error
      if (data) {
        setSettings({
          include_schedule: data.include_schedule,
          include_coach_notes: data.include_coach_notes,
          tone: data.tone,
          length: data.length,
        })
      }
    } catch (err) {
      console.error('Error fetching AI settings:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    setSuccess(false)
    try {
      const { error } = await supabase
        .from('ai_settings')
        .update({
          ...settings,
          updated_at: new Date().toISOString(),
        })
        .eq('team_id', teamId)

      if (error) throw error
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving AI settings:', err)
      alert('Failed to save AI preferences')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8 text-brand-slate">
          Loading AI preferences...
        </div>
      </Card>
    )
  }

  const toneOptions = [
    { value: 'casual', label: 'Casual - Friendly and conversational' },
    { value: 'professional', label: 'Professional - Formal and polished' },
    { value: 'energetic', label: 'Energetic - Upbeat and enthusiastic' },
  ]

  const lengthOptions = [
    { value: 'brief', label: 'Brief - Quick and to the point' },
    { value: 'standard', label: 'Standard - Balanced detail' },
    { value: 'detailed', label: 'Detailed - Comprehensive information' },
  ]

  return (
    <Card>
      <div className="mb-6">
        <h2 className="font-heading text-xl font-bold mb-2">
          AI Preferences
        </h2>
        <p className="text-sm text-brand-slate">
          Control how AI generates your weekly dispatches. These settings help maintain consistent tone and style.
        </p>
      </div>

      <div className="space-y-6">
        {/* Toggle Options */}
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-brand-charcoal mb-1">
                Include Upcoming Schedule
              </h3>
              <p className="text-sm text-brand-slate">
                Automatically add next week's games and practices from your calendar
              </p>
            </div>
            <button
              onClick={() => handleToggle('include_schedule')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                settings.include_schedule ? 'bg-brand-green' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.include_schedule ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-brand-charcoal mb-1">
                Include Coach's Notes
              </h3>
              <p className="text-sm text-brand-slate">
                Look for and include coaching feedback or notes in the dispatch
              </p>
            </div>
            <button
              onClick={() => handleToggle('include_coach_notes')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                settings.include_coach_notes ? 'bg-brand-green' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.include_coach_notes ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Tone Selection */}
        <div>
          <label className="label">Email Tone</label>
          <select
            value={settings.tone}
            onChange={(e) => handleChange('tone', e.target.value)}
            className="select"
          >
            {toneOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Length Selection */}
        <div>
          <label className="label">Email Length</label>
          <select
            value={settings.length}
            onChange={(e) => handleChange('length', e.target.value)}
            className="select"
          >
            {lengthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Tip:</strong> AI learns from your previous dispatches to maintain consistent voice. The more you use it, the better it matches your style!
        </p>
      </div>

      {success && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          AI preferences saved successfully!
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save AI Preferences'}
        </Button>
      </div>
    </Card>
  )
}

export default AISteeringPanel


